import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { DataResult } from '@progress/kendo-data-query';
import { catchError, concatMap, debounceTime, exhaustMap, map, Observable, of, switchMap, tap } from 'rxjs';
import { FetchArgs } from 'src/app/shared/models/core';
import { IntegrationDashboardActions } from './integration-dashboard.actions';
import { IntegrationManagerService } from '../../services/integration-manager.service';
import { integrationDashboardInitialState } from './integration-dashboard.reducers';
import { IntegrationArtifact } from '../../models/integration-artifact';
import { Store } from '@ngrx/store';
import { IntegrationDashboardSelectors } from './integration-dashboard.selectors';

@Injectable()
export class IntegrationDashboardEffects {
    getAllIntegrationEventLogs$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(IntegrationDashboardActions.getAllIntegrationEventLogs),
            debounceTime(500),
            concatLatestFrom(() => this.store.select(IntegrationDashboardSelectors.selectIntegrationDashboardArgs)),
            switchMap(([action, args]) => this.getAllIntegrationEventLogs(args)),
            concatMap((logs) =>
                of(IntegrationDashboardActions.setAllIntegrationEventLogs({ integrationEventLogList: logs }))
            )
        );
    });

    downloadIntegrationArtifact$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(IntegrationDashboardActions.downloadIntegrationArtifact),
            switchMap((action) =>
                this.downloadIntegrationArtifact(action.artifact).pipe(
                    map((file) =>
                        IntegrationDashboardActions.integrationArtifactDownloaded({ file, artifact: action.artifact })
                    )
                )
            )
        );
    });

    integrationArtifactDownloaded$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(IntegrationDashboardActions.integrationArtifactDownloaded),
            tap((action) => this.managerService.downloadFile(action.file, action.artifact)),
            map(() => IntegrationDashboardActions.integrationArtifactDownloadCompleted())
        );
    });

    setArgs$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(IntegrationDashboardActions.setArgs),
            map(() => IntegrationDashboardActions.getAllIntegrationEventLogs())
        );
    });

    setStatus$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(IntegrationDashboardActions.setStatus),
            map(() => IntegrationDashboardActions.getAllIntegrationEventLogs())
        );
    });

    runDOPsPipeline$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(IntegrationDashboardActions.runDOPsPipeline),
            exhaustMap((action) => {
                return this.managerService.runDOPsPipeline(action.integration).pipe(
                    map(() => IntegrationDashboardActions.runDOPsPipelineSuccess()),
                    catchError((error: unknown) => of(IntegrationDashboardActions.runDOPsPipelineFailure({ error })))
                );
            })
        );
    });

    constructor(
        private readonly store: Store,
        private readonly actions$: Actions,
        private readonly managerService: IntegrationManagerService
    ) {}

    getAllIntegrationEventLogs = (args: FetchArgs): Observable<DataResult> =>
        this.managerService
            .getIntegrationEventLogs(args)
            .pipe(catchError(() => of(integrationDashboardInitialState.integrationEventLogList)));

    downloadIntegrationArtifact = (artifact: IntegrationArtifact): Observable<any> =>
        this.managerService
            .downloadIntegrationArtifact(artifact)
            .pipe(catchError(() => of(integrationDashboardInitialState.integrationEventLogList)));
}
